// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { getCurrentUser, signIn as amplifySignIn, signOut as amplifySignOut } from "@aws-amplify/auth";
import { API_BASE } from "../config/api";

// 1) Create the Context
const AuthContext = createContext();

// 2) Build the Provider
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, attempt to load the current authenticated user
  useEffect(() => {
    getCurrentUser()
      .then((u) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  // Sign-in method
  const login = async (username, password) => {
    // console.log("AuthContext: Attempting amplifySignIn...");
    try {
      const cognitoUserFromSignIn = await amplifySignIn({ username, password });
      // console.log("AuthContext: amplifySignIn successful. cognitoUserFromSignIn:", cognitoUserFromSignIn);

      // Check for NEW_PASSWORD_REQUIRED challenge
      if (
        cognitoUserFromSignIn.nextStep &&
        cognitoUserFromSignIn.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        // console.log("AuthContext: NEW_PASSWORD_REQUIRED challenge detected. Throwing custom error.");
        // Throw a custom error object that Login.js can catch
        // Pass the cognitoUserFromSignIn object as it contains necessary details for confirmSignIn
        // eslint-disable-next-line no-throw-literal
        throw {
          code: "NewPasswordRequired",
          cognitoUser: cognitoUserFromSignIn, // Pass the user object from signIn
          message: "New password required.",
        };
      }

      // console.log("AuthContext: No NEW_PASSWORD_REQUIRED challenge, or challenge already handled. Attempting getCurrentUser...");
      // If no challenge, or challenge handled, proceed to get current authenticated user
      const currentUser = await getCurrentUser();

      // Check if user is archived before allowing login
      const sub = currentUser.username;
      try {
        // First get the user's profile to get their ID
        const profileRes = await fetch(
          `${API_BASE}/users/${sub}`
        );
        if (profileRes.ok) {
          const profile = await profileRes.json();
          const userId = profile.id;

          // Now fetch the initial data which contains the users list with is_archived
          const initialRes = await fetch(
            `${API_BASE}/initial/${sub}`
          );
          if (initialRes.ok) {
            const initialData = await initialRes.json();
            const userRecord = (initialData.users || []).find(u => u.id === userId);

            if (userRecord && userRecord.is_archived) {
              // Sign out the user immediately
              await amplifySignOut();
              // eslint-disable-next-line no-throw-literal
              throw {
                code: "UserArchived",
                message: "This account has been archived and cannot log in. Please contact your administrator.",
              };
            }
          }
        }
      } catch (err) {
        // If it's our custom UserArchived error, re-throw it
        if (err.code === "UserArchived") {
          throw err;
        }
        // Otherwise, log the error but don't block login if fetch fails
        console.warn("Failed to check user archived status:", err);
      }

      setUser(currentUser);
      // console.log("AuthContext: getCurrentUser successful. User set:", currentUser);
      return currentUser;
    } catch (error) {
      console.error("AuthContext: Error during login process", error);
      throw error; // Re-throw the error to be caught by the calling component
    }
  };

  // Sign-out method
  const logout = async () => {
    try {
      await amplifySignOut();
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// 3) Custom hook for consuming context
export const useAuth = () => useContext(AuthContext);
